// react
import { useCallback, useContext, useState } from 'react';

// exceljs
import { Workbook } from 'exceljs';

// types
import {
	Account,
	AdvanceTotal,
	CashInMethod,
	CashInTotal,
	CashOutTotal,
	Category,
	Component,
	ErrorCause,
	Ifr,
	IfrPayload,
	IfrPlan,
	IfrPlanVersion,
	IfrPlanVersionPayload,
	IfrTable,
	IfrTables,
	LoadingStatus,
	Project,
} from '../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../App.context';
import { ContextIfr } from '../components/pages/Project/Project.context';

// ui
import { OnClickCellParams } from '../components/ui/TableSheet/TableSheet';

// utils
import { hexToArgb } from '../utils/color';
import { downloadFile, downloadZip } from '../utils/files';
import { formatDate } from '../utils/dates';
import {
	getIfrTableTitle,
	ifrTablesToCsvFiles,
	ifrTablesToPdfTables,
	sanitizeIfrTableRowsForExport,
} from '../utils/ifrs';

// pages
import { GridColDefIfr } from '../components/pages/Project/Ifr/Ifr';

// theme
import { theme } from '../theme';

// props
export interface PatchIfrProps {
	ifrId: Ifr['id'] | undefined;
	ifrPayload: IfrPayload | null;
	onSuccess?: ({ ifr }: { ifr: Ifr }) => void;
}

export interface PatchIfrPlanVersionProps {
	ifrPlanId: IfrPlan['id'] | undefined;
	ifrPlanVersionId: IfrPlanVersion['id'] | undefined;
	ifrPlanVersionPayload: IfrPlanVersionPayload | null;
	onSuccess?: ({ ifrPlanVersion }: { ifrPlanVersion: IfrPlanVersion }) => void;
}

export interface PostIfrPlanVersionProps {
	ifrPlanId: IfrPlan['id'] | undefined;
	ifrPlanVersionId: IfrPlanVersion['id'] | undefined;
	ifrPlanVersionPayload: IfrPlanVersionPayload | null;
	onSuccess?: ({ ifrPlanVersion }: { ifrPlanVersion: IfrPlanVersion }) => void;
}

export interface CalculateLoanClosingCashBalanceProps {
	cashIn?: IfrTable;
	cashOutCategories?: IfrTable;
	loanId: number;
	openingBalances?: IfrTable;
}

export interface HandleExportPdfProps {
	ifrTables: IfrTables;
	project: Project | null;
}

export interface HandleExportCsvProps {
	ifrTables: IfrTables;
	project: Project | null;
}

export interface HandleExportXlsProps {
	ifrTables: IfrTables;
	project: Project | null;
}

export const useIfr = () => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { ifr } = useContext(ContextIfr);

	// state
	const [status, setStatus] = useState<LoadingStatus>('DEFAULT');

	const patchIfrPlanVersion = async ({
		ifrPlanId,
		ifrPlanVersionId,
		ifrPlanVersionPayload,
		onSuccess,
	}: PatchIfrPlanVersionProps) => {
		setStatus('LOADING');

		try {
			if (ifrPlanVersionPayload) {
				const fetchPatchIfrPlanVersion = await fetch(
					`${process.env.REACT_APP_API_URL}/ifrs/plans/${ifrPlanId}/versions/${ifrPlanVersionId}`,
					{
						method: 'PATCH',
						headers: {
							Authorization: `Bearer ${accessToken}`,
							User: String(idToken),
						},
						body: JSON.stringify(ifrPlanVersionPayload),
					}
				);
				const resPatchIfrPlanVersion = await fetchPatchIfrPlanVersion.json();
				if (resPatchIfrPlanVersion.error) {
					throw new Error('Failed to patch IFR plan version', {
						cause: {
							id: resPatchIfrPlanVersion.error?.id,
						},
					});
				}

				if (resPatchIfrPlanVersion.data) {
					setStatus('SUCCESS');
					setBanner({
						hasClose: true,
						severity: 'success',
						message: t('alert.ifrPlanVersionUpdated'),
					});
					onSuccess?.({ ifrPlanVersion: resPatchIfrPlanVersion.data });
				}
			} else {
				throw new Error('IFR plan version is undefined');
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			let message = t('error.default');

			if (cause) {
				if (cause.id === 'IFR_PLAN_VERSION_DATA_IMMUTABLE') {
					message = t('error.ifrPlanVersionDataImmutable');
				} else if (cause.id === 'IFR_PLAN_VERSION_DATA_INVALID_FOREIGN_KEY') {
					message = t('error.ifrPlanVersionDataInvalidForeignKey');
				} else if (cause.id === 'IFR_PLAN_VERSION_DATA_INVALID_MONTH') {
					message = t('error.ifrPlanVersionDataInvalidMonth');
				} else if (cause.id === 'IFR_PLAN_VERSION_DATA_INVALID_SINGLE_REFERENCE') {
					message = t('error.ifrPlanVersionDataInvalidSingleReference');
				} else if (cause.id === 'IFR_PLAN_VERSION_UNAUTHORIZED_PATCH') {
					message = t('error.unauthorized');
				}
			}
			setStatus('ERROR');
			setBanner({
				hasClose: true,
				severity: 'error',
				message,
			});
		}
	};

	const patchIfr = async ({ ifrId, ifrPayload, onSuccess }: PatchIfrProps) => {
		setStatus('LOADING');

		try {
			if (ifrId && ifrPayload) {
				const fetchPatchIfr = await fetch(
					`${process.env.REACT_APP_API_URL}/ifrs/reports/${ifrId}`,
					{
						method: 'PATCH',
						headers: {
							Authorization: `Bearer ${accessToken}`,
							User: String(idToken),
						},
						body: JSON.stringify(ifrPayload),
					}
				);

				const resPatchIfr = await fetchPatchIfr.json();

				if (resPatchIfr.error) {
					throw new Error('Failed to IFR', {
						cause: {
							id: resPatchIfr.error?.id,
						},
					});
				}

				if (resPatchIfr.data) {
					setStatus('SUCCESS');
					setBanner({
						hasClose: true,
						severity: 'success',
						message: t('alert.ifrUpdated'),
					});
					onSuccess?.({ ifr: resPatchIfr.data });
				}
			} else {
				throw new Error('IFR ID or IFR payload is undefined');
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			let message = t('error.default');

			if (cause) {
				if (cause.id === 'IFR_UNAUTHORIZED_PATCH') {
					message = t('error.unauthorized');
				}
			}
			setStatus('ERROR');
			setBanner({
				hasClose: true,
				severity: 'error',
				message,
			});
		}
	};

	const postIfrPlanVersion = async ({
		ifrPlanId,
		ifrPlanVersionPayload,
		onSuccess,
	}: PostIfrPlanVersionProps) => {
		setStatus('LOADING');
		try {
			const fetchPostIfrPlanVersion = await fetch(
				`${process.env.REACT_APP_API_URL}/ifrs/plans/${ifrPlanId}/versions`,
				{
					method: 'POST',
					headers: {
						Authorization: `Bearer ${accessToken}`,
						'Content-Type': 'application/json',
						User: String(idToken),
					},
					body: JSON.stringify(ifrPlanVersionPayload),
				}
			);
			const resPostIfrPlanVersion = await fetchPostIfrPlanVersion.json();
			if (resPostIfrPlanVersion.error) {
				throw new Error('Failed to create IFR plan version', {
					cause: {
						id: resPostIfrPlanVersion.error?.id,
					},
				});
			}
			if (resPostIfrPlanVersion.data) {
				setStatus('SUCCESS');
				setBanner({
					hasClose: true,
					message: t('alert.ifrPlanVersionCreated'),
					severity: 'success',
				});
				onSuccess?.({ ifrPlanVersion: resPostIfrPlanVersion.data[0] });
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;
			let message = t('error.default');
			if (cause) {
				if (cause.id === 'IFR_PLAN_VERSION_UNAUTHORIZED_POST') {
					message = t('error.unauthorized');
				} else if (cause.id === 'IFR_PLAN_VERSION_DRAFT_EXISTS') {
					message = t('error.ifrPlanVersionDraftExists');
				} else if (cause.id === 'IFR_PLAN_NOT_FOUND') {
					message = t('error.ifrPlanNotFound');
				}
			}
			setStatus('ERROR');
			setBanner({
				hasClose: true,
				message,
				severity: 'error',
			});
		}
	};

	const calculateLoanClosingCashBalance = useCallback(
		({
			cashIn,
			cashOutCategories,
			loanId,
			openingBalances,
		}: CalculateLoanClosingCashBalanceProps) => {
			let loanClosingCashBalance: number | undefined;

			if (loanId) {
				const rowOpeningBalances = openingBalances?.rows?.find(
					(row) => row.id === 'opening-balances'
				);
				const rowCashInTotals = cashIn?.rows?.find((row) => row.id === 'totals-columns');
				const rowCashOutTotals = cashOutCategories?.rows?.find(
					(row) => row.id === 'totals-columns'
				);

				// define total in as opening balance + cash in
				const totalIn =
					(rowOpeningBalances?.[`loan${loanId}`] || 0) +
					(rowCashInTotals?.[`loan${loanId}Paid`] || 0);

				// define closing cash balance as total in - cash out
				loanClosingCashBalance = totalIn - (rowCashOutTotals?.[`loan${loanId}Paid`] || 0);
			}

			return loanClosingCashBalance;
		},
		[]
	);

	const handleClickCell = ({ params }: OnClickCellParams) => {
		const colDef = params.colDef as GridColDefIfr;
		const loan = colDef?.loan;
		const isAccumulated = params.field.includes('Accumulated');

		let transactions: number[] = [];

		// define transactions as account advances, cash in or cash out
		if (loan) {
			if (params.row?.account) {
				const account = params.row?.account as Account;
				let advances: AdvanceTotal[] = [];
				if (isAccumulated) {
					advances = account.advancesAccumulated || [];
				} else {
					advances = account.advances || [];
				}
				transactions = advances.find((a) => a.loan === loan?.id)?.transactions || [];
				searchParams.set('dialogAccount', String(account.id));
			}

			if (params.row?.cashInMethod) {
				const cashInMethod = params.row?.cashInMethod as CashInMethod;
				let cashIn: CashInTotal[] = [];
				if (isAccumulated) {
					cashIn = cashInMethod.cashInAccumulated || [];
				} else {
					cashIn = cashInMethod.cashIn || [];
				}
				transactions = cashIn.find((c) => c.loan === loan?.id)?.transactions || [];
				searchParams.set('dialogCashInMethod', String(cashInMethod.id));
			}

			if (params.row?.category) {
				const category = params.row?.category as Category;
				let cashOut: CashOutTotal[] = [];
				if (isAccumulated) {
					cashOut = category.cashOutAccumulated || [];
				} else {
					cashOut = category.cashOut || [];
				}
				transactions = cashOut.find((c) => c.loan === loan?.id)?.transactions || [];
				searchParams.set('dialogCategory', String(category.id));
			}

			if (params.row?.component) {
				const component = params.row?.component as Component;
				let cashOut: CashOutTotal[] = [];
				if (isAccumulated) {
					cashOut = component.cashOutAccumulated || [];
				} else {
					cashOut = component.cashOut || [];
				}
				transactions = cashOut.find((c) => c.loan === loan?.id)?.transactions || [];
				searchParams.set('dialogComponent', String(component.id));
			}
		}

		// open transactions dialog
		searchParams.set('dialog', 'dialogTransactions');

		// add ifr date range if not accumulated
		if (ifr && !isAccumulated) {
			// set dialogCashOutDateEnd and dialogCashOutDateStart for /categories and /components
			// set dialogDateStart and dialogDateEnd for applied /transactions
			if (ifr.dateEnd) {
				const dateEndFormatted = formatDate({ date: ifr.dateEnd, format: 'YYYY-MM-DD' });
				searchParams.set('dialogCashOutDateEnd', dateEndFormatted);
				searchParams.set('dialogDateEnd', dateEndFormatted);
			}

			if (ifr.dateStart) {
				const dateStartFormatted = formatDate({
					date: new Date(ifr.dateStart),
					format: 'YYYY-MM-DD',
				});
				searchParams.set('dialogCashOutDateStart', dateStartFormatted);
				searchParams.set('dialogDateStart', dateStartFormatted);
			}
		}

		if (loan) {
			searchParams.set('dialogLoan', String(loan?.id));
		}

		if (transactions.length) {
			searchParams.set('dialogTransactions', transactions.join(','));
		}
		setSearchParams(searchParams);
	};

	const handleExportPdf = async ({ ifrTables, project }: HandleExportPdfProps) => {
		try {
			if (!project) {
				throw new Error('Project is undefined', {
					cause: {
						id: 'PROJECT_NON_EXISTENT',
					},
				});
			}

			setBanner(null);
			setStatus('LOADING');

			// Convert IfrTables to PDF format
			const tables = ifrTablesToPdfTables({ ifrTables, t });

			// Make request to PDF service
			const response = await fetch(`${process.env.REACT_APP_API_URL}/services/pdfs`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
				body: JSON.stringify({
					options: {
						pageOrientation: 'landscape',
					},
					tables,
					header: {
						title: t('ifr'),
						subtitle: `${project.name} (${project.idWbg})`,
					},
				}),
			});

			const result = await response.json();

			if (result.error) {
				throw new Error('Failed to create PDF', {
					cause: {
						id: result.error?.id || 'PDF_FAILED',
					},
				});
			}

			// Download the PDF
			const dateString = formatDate({ date: new Date(), format: 'YYYY-MM-DD' });
			const filename = `${project.idWbg}-ifr-${dateString}.pdf`;

			downloadFile({
				data: new Uint8Array(result?.data?.blob?.data).buffer,
				name: filename,
				type: 'application/pdf',
			});

			setStatus('SUCCESS');
			setBanner({
				hasClose: true,
				severity: 'success',
				message: t('alert.reportDownloaded'),
			});
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;
			setStatus('ERROR');

			let message = t('error.default');
			if (cause) {
				if (cause.id === 'PDF_FAILED') {
					message = t('error.pdfFailed');
				} else if (cause.id === 'PROJECT_NON_EXISTENT') {
					message = t('error.projectNonExistent');
				}
			}
			setBanner({
				hasClose: true,
				severity: 'error',
				message,
			});
		}
	};

	const handleExportCsv = async ({ ifrTables, project }: HandleExportCsvProps) => {
		try {
			if (!project) {
				throw new Error('Project is undefined', {
					cause: {
						id: 'PROJECT_NON_EXISTENT',
					},
				});
			}

			setBanner(null);
			setStatus('LOADING');

			// Convert IfrTables to CSV files
			const csvFiles = ifrTablesToCsvFiles({ ifrTables, project });

			if (csvFiles.length === 0) {
				throw new Error('No data to export', {
					cause: {
						id: 'EXPORT_NO_DATA',
					},
				});
			}

			// Create zip file and download
			const dateString = formatDate({ date: new Date(), format: 'YYYY-MM-DD' });
			const zipFilename = `${project.idWbg}-ifr-${dateString}.zip`;

			await downloadZip({
				files: csvFiles,
				zipFilename,
			});

			setStatus('SUCCESS');
			setBanner({
				hasClose: true,
				severity: 'success',
				message: t('alert.reportDownloaded'),
			});
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;
			setStatus('ERROR');

			let message = t('error.default');
			if (cause) {
				if (cause.id === 'EXPORT_NO_DATA') {
					message = t('error.noDataToExport');
				} else if (cause.id === 'PROJECT_NON_EXISTENT') {
					message = t('error.projectNonExistent');
				}
			}
			setBanner({
				hasClose: true,
				severity: 'error',
				message,
			});
		}
	};

	const handleExportXls = async ({ ifrTables, project }: HandleExportXlsProps) => {
		try {
			if (!project) {
				throw new Error('Project is undefined', {
					cause: {
						id: 'PROJECT_NON_EXISTENT',
					},
				});
			}

			setBanner(null);
			setStatus('LOADING');

			// Create a new workbook
			const workbook = new Workbook();

			// Track if any data was added
			let hasData = false;

			Object.entries(ifrTables).forEach(([key, table]: [string, IfrTable]) => {
				if (table.rows && table.rows.length > 0) {
					// Sanitize rows
					const rows = sanitizeIfrTableRowsForExport({ rows: table.rows, key });

					if (rows.length > 0) {
						hasData = true;

						// Create sheet name from table key
						const sheetName = getIfrTableTitle({ key, t });
						const worksheet = workbook.addWorksheet(sheetName);

						// Add headers
						const headers = table.columns.map((col) => col.headerName || col.field || '');
						worksheet.addRow(headers);

						// Style the header row
						const headerRow = worksheet.getRow(1);
						headerRow.font = { bold: true };
						headerRow.fill = {
							type: 'pattern',
							pattern: 'solid',
							fgColor: { argb: hexToArgb(theme.palette.brand.grey[200]) },
						};

						// Add data rows
						rows.forEach((row) => {
							const rowData = table.columns.map((col) => {
								const value = row[col.field];
								if (value === null || value === undefined) {
									return '';
								}
								return value;
							});
							worksheet.addRow(rowData);
						});

						// Auto-fit columns
						worksheet.columns.forEach((column) => {
							if (column && typeof column === 'object' && 'eachCell' in column) {
								let maxLength = 0;
								column.eachCell?.({ includeEmpty: true }, (cell) => {
									const columnLength = cell.value ? cell.value.toString().length : 10;
									if (columnLength > maxLength) {
										maxLength = columnLength;
									}
								});
								column.width = maxLength < 10 ? 10 : maxLength;
							}
						});
					}
				}
			});

			if (!hasData) {
				throw new Error('No data to export', {
					cause: {
						id: 'EXPORT_NO_DATA',
					},
				});
			}

			const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

			// Generate Excel file
			const buffer = await workbook.xlsx.writeBuffer();
			const blob = new Blob([buffer], {
				type: fileType,
			});

			// Create filename
			const dateString = formatDate({ date: new Date(), format: 'YYYY-MM-DD' });
			const filename = `${project.idWbg}-ifr-${dateString}.xlsx`;

			// Download file
			downloadFile({
				data: blob,
				name: filename,
				type: fileType,
			});

			setStatus('SUCCESS');
			setBanner({
				hasClose: true,
				severity: 'success',
				message: t('alert.reportDownloaded'),
			});
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;
			setStatus('ERROR');

			let message = t('error.default');
			if (cause) {
				if (cause.id === 'EXPORT_NO_DATA') {
					message = t('error.noDataToExport');
				} else if (cause.id === 'PROJECT_NON_EXISTENT') {
					message = t('error.projectNonExistent');
				}
			}
			setBanner({
				hasClose: true,
				severity: 'error',
				message,
			});
		}
	};

	return {
		calculateLoanClosingCashBalance,
		handleClickCell,
		handleExportCsv,
		handleExportPdf,
		handleExportXls,
		patchIfr,
		patchIfrPlanVersion,
		postIfrPlanVersion,
		status,
	};
};
